"use client"

import { createContext, useContext, useReducer, useCallback, type ReactNode } from "react"
import type { SavedView, FilterDto, StageFilterDto } from "@/types/saved-view"
import type { FieldConfiguration } from "@/types/workflow"

// ─── Types ───────────────────────────────────────────────────────────

export type ViewManagerStep = 'views' | 'columns' | 'fields' | 'filters' | 'custom-fields'

export interface AuthorizedClient {
  entityId: number
  name: string
}

export interface ViewManagerState {
  // Currently selected view
  selectedView: SavedView | null
  originalConfig: any | null // Snapshot for dirty detection (WorkflowHubTenantConfigurations)

  // Original snapshot for dirty detection
  originalAuthorizedClients: AuthorizedClient[]

  // Draft changes
  draftViewTitle: string
  draftIsPublic: boolean
  draftIsClientFacing: boolean
  /**
   * Per-view opt-in for the Excel export feature. AND'd with the user's
   * `ExportData` authorized action on the grid — both must be true for the
   * Export button to surface. Editable from the Views step (top-level view
   * metadata) and the columns/data step (so admins can flip it next to where
   * they curate the column set). Persisted to the BE on save and fanned out
   * across Requests-group siblings.
   */
  draftAllowDataExport: boolean
  draftAuthorizedClients: AuthorizedClient[]
  draftStageColumns: Record<string, string[]> // stageId → ordered fieldConfigurationIds
  draftGlobalFilters: FilterDto[]
  draftStageFilters: StageFilterDto[]
  draftSortByStage: Record<string, { columnCode: string; direction: 'asc' | 'desc' } | null>
  draftFieldChanges: Record<string, Partial<FieldConfiguration>> // fieldId → changed props
  draftColumnWidths: Record<string, number>

  // UI state
  activeStep: ViewManagerStep
  activeEditStage: string // which stage is being edited in columns/fields/filters
  selectedFieldId: string | null // which field's side panel is open
  isAddingCustomField: boolean // true when adding a new custom field via side panel
  showPreview: boolean // shared collapse state for table preview
  isLoading: boolean
  isSaving: boolean
  error: string | null
}

export type ViewManagerAction =
  | { type: 'LOAD_VIEW'; payload: { view: SavedView; config: any; authorizedClients?: AuthorizedClient[] } }
  | { type: 'SET_STEP'; payload: ViewManagerStep }
  | { type: 'SET_ACTIVE_STAGE'; payload: string }
  | { type: 'REORDER_COLUMNS'; payload: { stageId: string; fieldIds: string[] } }
  | { type: 'TOGGLE_COLUMN'; payload: { stageId: string; fieldId: string } }
  | { type: 'APPLY_TO_STAGES'; payload: { sourceStageId: string; targetStageIds: string[] } }
  | { type: 'UPDATE_FIELD'; payload: { fieldId: string; changes: Partial<FieldConfiguration> } }
  | { type: 'SET_GLOBAL_FILTERS'; payload: FilterDto[] }
  | { type: 'SET_STAGE_FILTERS'; payload: StageFilterDto[] }
  | { type: 'SET_STAGE_SORT'; payload: { stageId: string; sort: { columnCode: string; direction: 'asc' | 'desc' } | null } }
  | { type: 'UPDATE_VIEW_META'; payload: { title?: string; isPublic?: boolean; isClientFacing?: boolean; allowDataExport?: boolean; authorizedClients?: AuthorizedClient[] } }
  | { type: 'UPDATE_FIELD_METADATA'; payload: { fieldsInUse: any[]; stageConfigurations: any[] } }
  | { type: 'SELECT_FIELD'; payload: string | null }
  | { type: 'SET_ADDING_CUSTOM_FIELD'; payload: boolean }
  | { type: 'SET_SHOW_PREVIEW'; payload: boolean }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_COLUMN_WIDTH'; payload: { fieldId: string; width: number } }
  | { type: 'RESET' }

// ─── Initial State ───────────────────────────────────────────────────

const initialState: ViewManagerState = {
  selectedView: null,
  originalConfig: null,
  originalAuthorizedClients: [],
  draftViewTitle: '',
  draftIsPublic: false,
  draftIsClientFacing: false,
  draftAllowDataExport: false,
  draftAuthorizedClients: [],
  draftStageColumns: {},
  draftGlobalFilters: [],
  draftStageFilters: [],
  draftSortByStage: {},
  draftFieldChanges: {},
  draftColumnWidths: {},
  activeStep: 'views',
  activeEditStage: '',
  selectedFieldId: null,
  isAddingCustomField: false,
  showPreview: true,
  isLoading: false,
  isSaving: false,
  error: null,
}

// ─── Reducer ─────────────────────────────────────────────────────────

function viewManagerReducer(state: ViewManagerState, action: ViewManagerAction): ViewManagerState {
  switch (action.type) {
    case 'LOAD_VIEW': {
      const { view, config, authorizedClients = [] } = action.payload
      const stageColumns: Record<string, string[]> = {}
      const stages = config?.stageConfigurations || []

      // Initialize draft stage columns from the loaded configuration
      for (const stage of stages) {
        const stageId = stage.stageId || stage.stageCode
        stageColumns[stageId] = (stage.fieldConfigurations || []).map(
          (f: any) => f.fieldConfigurationId
        )
      }

      const firstStageId = stages.length > 0 ? (stages[0].stageId || stages[0].stageCode) : ''

      // Restore saved sort state per stage
      const sortByStage: Record<string, { columnCode: string; direction: 'asc' | 'desc' } | null> = {}
      for (const sc of stages) {
        const sid = sc.stageId || sc.stageCode
        const col = sc.sortColumn
        const dir = sc.sortDirection
        sortByStage[sid] = col && dir ? { columnCode: col, direction: dir as 'asc' | 'desc' } : null
      }

      return {
        ...state,
        selectedView: view,
        originalConfig: config,
        originalAuthorizedClients: authorizedClients,
        draftViewTitle: view.title,
        draftIsPublic: view.isPublic ?? false,
        draftIsClientFacing: view.isClientFacing ?? false,
        // Seed from the BE — undefined on legacy views (no FE editor exposed
        // the field before this change), which collapses to false (no export).
        // Admins explicitly toggle on per view.
        draftAllowDataExport: view.allowDataExport ?? false,
        draftAuthorizedClients: [...authorizedClients],
        draftStageColumns: stageColumns,
        draftGlobalFilters: config?.savedGlobalFilters || [],
        draftStageFilters: config?.savedStageFilters || [],
        draftSortByStage: sortByStage,
        draftFieldChanges: {},
        draftColumnWidths: config?.columnWidths ? JSON.parse(config.columnWidths) : {},
        activeEditStage: firstStageId,
        selectedFieldId: null,
        isLoading: false,
        error: null,
      }
    }

    case 'SET_STEP':
      return { ...state, activeStep: action.payload, selectedFieldId: null }

    case 'SET_ACTIVE_STAGE':
      return { ...state, activeEditStage: action.payload, selectedFieldId: null }

    case 'REORDER_COLUMNS': {
      const { stageId, fieldIds } = action.payload
      return {
        ...state,
        draftStageColumns: { ...state.draftStageColumns, [stageId]: fieldIds },
      }
    }

    case 'TOGGLE_COLUMN': {
      const { stageId, fieldId } = action.payload
      const current = state.draftStageColumns[stageId] || []
      const exists = current.includes(fieldId)
      const updated = exists
        ? current.filter(id => id !== fieldId)
        : [...current, fieldId]
      return {
        ...state,
        draftStageColumns: { ...state.draftStageColumns, [stageId]: updated },
      }
    }

    case 'APPLY_TO_STAGES': {
      const { sourceStageId, targetStageIds } = action.payload
      const sourceColumns = state.draftStageColumns[sourceStageId] || []
      const updated = { ...state.draftStageColumns }
      for (const targetId of targetStageIds) {
        updated[targetId] = [...sourceColumns]
      }
      return { ...state, draftStageColumns: updated }
    }

    case 'UPDATE_FIELD': {
      const { fieldId, changes } = action.payload
      return {
        ...state,
        draftFieldChanges: {
          ...state.draftFieldChanges,
          [fieldId]: { ...(state.draftFieldChanges[fieldId] || {}), ...changes },
        },
      }
    }

    case 'SET_GLOBAL_FILTERS':
      return { ...state, draftGlobalFilters: action.payload }

    case 'SET_STAGE_FILTERS':
      return { ...state, draftStageFilters: action.payload }

    case 'SET_STAGE_SORT':
      return {
        ...state,
        draftSortByStage: {
          ...state.draftSortByStage,
          [action.payload.stageId]: action.payload.sort,
        },
      }

    case 'UPDATE_VIEW_META': {
      const { title, isPublic, isClientFacing, allowDataExport, authorizedClients } = action.payload
      return {
        ...state,
        draftViewTitle: title ?? state.draftViewTitle,
        draftIsPublic: isPublic ?? state.draftIsPublic,
        draftIsClientFacing: isClientFacing ?? state.draftIsClientFacing,
        draftAllowDataExport: allowDataExport ?? state.draftAllowDataExport,
        draftAuthorizedClients: authorizedClients ?? state.draftAuthorizedClients,
      }
    }

    case 'UPDATE_FIELD_METADATA': {
      // Update field metadata (names, types, properties) without touching draft columns/filters
      const { fieldsInUse, stageConfigurations } = action.payload
      if (!state.originalConfig) return state

      // Merge fresh field data into stageConfigurations while preserving draft column order
      const updatedStageConfigs = (state.originalConfig.stageConfigurations || []).map((stage: any) => {
        const freshStage = stageConfigurations.find(
          (s: any) => (s.stageId || s.stageCode) === (stage.stageId || stage.stageCode)
        )
        if (!freshStage) return stage
        // Build field lookup from fresh data
        const freshFieldMap = new Map<string, any>()
        for (const f of (freshStage.fieldConfigurations || [])) freshFieldMap.set(f.fieldConfigurationId, f)
        for (const f of fieldsInUse) freshFieldMap.set(f.fieldConfigurationId, f)
        // Update existing field configs with fresh metadata
        return {
          ...stage,
          fieldConfigurations: (stage.fieldConfigurations || []).map((f: any) => {
            const fresh = freshFieldMap.get(f.fieldConfigurationId)
            return fresh ? { ...f, ...fresh } : f
          }),
        }
      })

      return {
        ...state,
        originalConfig: {
          ...state.originalConfig,
          fieldsInUse,
          stageConfigurations: updatedStageConfigs,
        },
      }
    }

    case 'SET_ADDING_CUSTOM_FIELD':
      return { ...state, isAddingCustomField: action.payload, selectedFieldId: action.payload ? null : state.selectedFieldId }

    case 'SET_SHOW_PREVIEW':
      return { ...state, showPreview: action.payload }

    case 'SELECT_FIELD':
      return { ...state, selectedFieldId: action.payload }

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }

    case 'SET_SAVING':
      return { ...state, isSaving: action.payload }

    case 'SET_ERROR':
      return { ...state, error: action.payload }

    case 'SET_COLUMN_WIDTH': {
      const { fieldId, width } = action.payload
      return {
        ...state,
        draftColumnWidths: { ...state.draftColumnWidths, [fieldId]: width },
      }
    }

    case 'RESET':
      return { ...initialState }

    default:
      return state
  }
}

// ─── Dirty Detection ─────────────────────────────────────────────────

function computeIsDirty(state: ViewManagerState): boolean {
  if (!state.originalConfig || !state.selectedView) return false

  // Check view metadata
  if (state.draftViewTitle !== state.selectedView.title) return true
  if (state.draftIsPublic !== (state.selectedView.isPublic ?? false)) return true
  if (state.draftIsClientFacing !== (state.selectedView.isClientFacing ?? false)) return true
  if (state.draftAllowDataExport !== (state.selectedView.allowDataExport ?? false)) return true

  // Check authorized clients changes
  const originalClientIds = (state.originalAuthorizedClients || []).map(c => c.entityId).sort()
  const draftClientIds = (state.draftAuthorizedClients || []).map(c => c.entityId).sort()
  if (JSON.stringify(originalClientIds) !== JSON.stringify(draftClientIds)) return true

  // Check field changes
  if (Object.keys(state.draftFieldChanges).length > 0) return true

  // Check stage column changes
  const originalStages = state.originalConfig?.stageConfigurations || []
  for (const stage of originalStages) {
    const stageId = stage.stageId || stage.stageCode
    const originalIds = (stage.fieldConfigurations || []).map((f: any) => f.fieldConfigurationId)
    const draftIds = state.draftStageColumns[stageId] || []
    if (JSON.stringify(originalIds) !== JSON.stringify(draftIds)) return true
  }

  // Check filter changes
  if (JSON.stringify(state.draftGlobalFilters) !== JSON.stringify(state.originalConfig?.savedGlobalFilters || [])) return true
  if (JSON.stringify(state.draftStageFilters) !== JSON.stringify(state.originalConfig?.savedStageFilters || [])) return true

  // Check sort changes
  for (const stage of originalStages) {
    const sid = stage.stageId || stage.stageCode
    const origSort = stage.sortColumn && stage.sortDirection
      ? { columnCode: stage.sortColumn, direction: stage.sortDirection }
      : null
    const draftSort = state.draftSortByStage[sid] || null
    if (JSON.stringify(origSort) !== JSON.stringify(draftSort)) return true
  }

  return false
}

// ─── Context ─────────────────────────────────────────────────────────

interface ViewManagerContextType {
  state: ViewManagerState
  dispatch: React.Dispatch<ViewManagerAction>
  isDirty: boolean
}

const ViewManagerContext = createContext<ViewManagerContextType | null>(null)

export function ViewManagerProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(viewManagerReducer, initialState)
  const isDirty = computeIsDirty(state)

  return (
    <ViewManagerContext.Provider value={{ state, dispatch, isDirty }}>
      {children}
    </ViewManagerContext.Provider>
  )
}

export function useViewManager() {
  const context = useContext(ViewManagerContext)
  if (!context) {
    throw new Error('useViewManager must be used within a ViewManagerProvider')
  }
  return context
}
